import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { Truck, CreditCard, QrCode } from "lucide-react";
import { Button } from "@/components/ui/button";
import { StatusBadge } from "@/components/StatusBadge";
import { NoteButton } from "@/components/NoteButton";
import { StatusTimeline } from "@/components/StatusTimeline";
import { OrderItems } from "@/components/OrderItems";
import { CashInfo } from "@/components/CashPayment";
import { StoreNotify } from "@/components/StoreNotify";
import { api, brl, fmtDate, formatError, paymentLabel, paymentStatusLabel } from "@/lib/api";

export function OrderDetail({ order }) {
  const [paying, setPaying] = useState(false);
  const navigate = useNavigate();
  const d = order.delivery || {};

  const payNow = async () => {
    setPaying(true);
    try {
      const { data } = await api.post("/payments/checkout", { order_id: order.id, method: "mercadopago", origin_url: window.location.origin });
      window.location.href = data.checkout_url;
    } catch (e) {
      toast.error(formatError(e));
      setPaying(false);
    }
  };

  return (
    <div className="space-y-6 rounded-md border border-slate-200 bg-white p-6" data-testid="order-detail">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <p className="text-xs uppercase tracking-wider text-slate-400">Pedido</p>
          <p className="font-mono text-2xl font-bold" data-testid="order-number">#{order.number}</p>
          <p className="text-xs text-slate-500">{fmtDate(order.created_at)}</p>
        </div>
        <div className="flex flex-wrap items-center gap-2">
          <StatusBadge status={order.status} />
          <NoteButton order={order} className="h-8 text-xs" />
        </div>
      </div>
      <StatusTimeline order={order} />
      <StoreNotify key={order.id} order={order} />
      {order.tracking_code && (
        <div className="flex items-center gap-3 rounded-md border border-sky-200 bg-sky-50 p-4" data-testid="order-tracking-code">
          <Truck className="h-5 w-5 text-sky-700" />
          <p className="text-sm">Código de rastreio: <span className="font-mono font-bold">{order.tracking_code}</span></p>
        </div>
      )}
      <OrderItems items={order.items} />
      {order.art_files?.length > 0 && (
        <p className="text-xs text-slate-500" data-testid="order-art-names">Arte enviada: {order.art_files.map((f) => f.name).join(", ")}</p>
      )}
      <div className="grid gap-4 border-t border-slate-100 pt-4 sm:grid-cols-2">
        <div className="text-sm text-slate-600">
          <p className="font-semibold text-slate-900">{d.method === "retirada" ? "Retirada na loja" : "Entrega"}</p>
          {d.method === "entrega" && <p>{d.address}, {d.number} {d.complement} — {d.district} · {d.city}/{d.state} · CEP {d.cep}</p>}
        </div>
        <div className="text-right">
          <p className="text-xs uppercase tracking-wider text-slate-400">Total</p>
          <p className="font-mono text-2xl font-bold" data-testid="order-total">{brl(order.total)}</p>
          <p className="text-xs text-slate-500" data-testid="order-payment-status">{paymentLabel(order.payment_method)} · {paymentStatusLabel(order.payment_status)}</p>
        </div>
      </div>
      <CashInfo order={order} />
      {order.payment_status !== "paid" && order.status === "Aguardando pagamento" && order.payment_method !== "cash" && (
        <div className="grid gap-2 sm:grid-cols-2">
          <Button onClick={() => navigate(`/pix/${order.id}`)} className="w-full rounded-full bg-[#00B1A9] hover:bg-[#009690]" data-testid="btn-pay-pix">
            <QrCode className="h-4 w-4" /> Pagar com Pix
          </Button>
          <Button onClick={payNow} disabled={paying} className="w-full rounded-full bg-[#E5007D] hover:bg-[#c2006a]" data-testid="btn-pay-now">
            <CreditCard className="h-4 w-4" /> {paying ? "Abrindo pagamento..." : "Cartão (Mercado Pago)"}
          </Button>
        </div>
      )}
    </div>
  );
}
